import styled, { keyframes } from 'styled-components';

/**
 * One look for "the AI is working", wherever it is said.
 *
 * Both assistants say it from a bubble in the thread, in the place their answer
 * will land: Viktor writes into his, and the merchant's support bot gets one of
 * its own that the arriving message replaces. Same sentence in the same size,
 * the same three dots at the same tempo, each new phase fading in where the
 * last one stood. Only the ink differs, and only because the bubbles do -
 * Viktor's is a gradient, the bot's is the plain white card every incoming
 * message wears.
 */

/** Which bubble the line is drawn in, which is the only thing that varies. */
export type ThinkingTone = 'gradient' | 'card';

const think = keyframes`
  0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-3px); }
`;

/**
 * The dots, which are the part people recognise: same size, gap and tempo in
 * both chats. They inherit their colour from the line, so they are never a
 * shade apart from the words they follow.
 */
export const ThinkingDots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  /* Matches the line height it replaces, so the empty bubble is exactly the
     height of the one-line bubble it becomes. */
  height: 20px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    animation: ${think} 1.4s ease-in-out infinite;
  }

  span:nth-child(2) {
    animation-delay: 0.2s;
  }
  span:nth-child(3) {
    animation-delay: 0.4s;
  }

  @media (max-width: 800px) {
    height: 22px;
  }
`;

/**
 * The line itself: the words on the left, the dots at the end of the sentence
 * rather than under it.
 *
 * The bubble's own ink at 85% in both tones - dimmer than a message, because
 * this is the assistant talking about an answer rather than the answer.
 */
export const ThinkingLine = styled.span<{
  $tone: ThinkingTone;
  $inline?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 20px;
  color: ${({ theme, $tone }) =>
    $tone === 'gradient' ? `${theme.white}D9` : `${theme.darkGray}D9`};
  /* Alone in the bubble it IS the bubble; under a half-written answer it is a
     second line and needs the gap between them. */
  margin-top: ${({ $inline }) => ($inline ? '6px' : '0')};
`;

const statusEnter = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/**
 * One phase's words. Remounted on every phase - see `ThinkingIndicator` - so
 * the new sentence fades in where the old one stood instead of swapping
 * between two frames.
 */
export const ThinkingLabel = styled.span`
  animation: ${statusEnter} 0.25s ease-out backwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
